import React, { useState, useEffect } from 'react';
import { User, Bell, Palette, Globe, Shield, Database, ChevronRight, Save, Lock, Key, AlertTriangle,EyeOff,Eye } from 'lucide-react';
import api from '../api/axios';

const Settings = () => {
    const [activeTab, setActiveTab] = useState('profile');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showCurrent, setShowCurrent] = useState(false);
const [showNew, setShowNew] = useState(false);
const [showConfirm, setShowConfirm] = useState(false);


    // Error states
    const [profileError, setProfileError] = useState('');
    const [preferencesError, setPreferencesError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const [exportError, setExportError] = useState('');

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        address: ''
    });

    const [preferences, setPreferences] = useState({
        currency: 'INR',
        dateFormat: 'DD/MM/YYYY',
        theme: 'light',
        notifications: {
            email: true,
            push: false
        }
    });

    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [accountStats, setAccountStats] = useState(null);

    useEffect(() => {
        fetchUserData();
    }, []);

    const fetchUserData = async () => {
        try {
            setIsLoading(true);

            const profileResponse = await api.get('/settings/profile');
            const profile = profileResponse.data.data;
            setFormData({
                name: profile.name || '',
                email: profile.email || '',
                phone: profile.phone || '',
                company: profile.company || '',
                address: profile.address || ''
            });

            const prefsResponse = await api.get('/settings/preferences');
            setPreferences(prefsResponse.data.data);

            const statsResponse = await api.get('/settings/stats');
            setAccountStats(statsResponse.data.data);

        } catch (error) {
            console.error('Error fetching user data:', error);
            setProfileError('Failed to load user data');
        } finally {
            setIsLoading(false);
        }
    };

    const tabs = [
        { id: 'profile', label: 'Profile', icon: User },
        { id: 'notifications', label: 'Notifications', icon: Bell },
        { id: 'appearance', label: 'Appearance', icon: Palette },
        { id: 'preferences', label: 'Preferences', icon: Globe },
        { id: 'security', label: 'Security', icon: Shield },
        { id: 'data', label: 'Data Management', icon: Database },
    ];

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handlePreferenceChange = (field, value) => {
        setPreferences(prev => ({ ...prev, [field]: value }));
    };

    const handleNotificationChange = (type, value) => {
        setPreferences(prev => ({
            ...prev,
            notifications: { ...prev.notifications, [type]: value }
        }));
    };

    const handleSaveProfile = async () => {
        try {
            setIsSaving(true);
            setProfileError('');
            await api.put('/settings/profile', formData);
            setProfileError('Profile updated successfully!');
        } catch (error) {
            console.error('Error saving profile:', error);
            setProfileError(error.response?.data?.error || 'Failed to update profile');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSavePreferences = async () => {
        try {
            setIsSaving(true);
            setPreferencesError('');
            await api.put('/settings/preferences', preferences);
            setPreferencesError('Preferences updated successfully!');
        } catch (error) {
            console.error('Error saving preferences:', error);
            setPreferencesError('Failed to update preferences');
        } finally {
            setIsSaving(false);
        }
    };

    const handleChangePassword = async () => {
        setPasswordError('');

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPasswordError('New passwords do not match!');
            return;
        }

        if (passwordData.newPassword.length < 6) {
            setPasswordError('Password must be at least 6 characters long');
            return;
        }

        try {
            setIsSaving(true);
            await api.put('/settings/password', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            });
            setPasswordError('Password changed successfully!');
            setShowPasswordModal(false);
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            console.error('Error changing password:', error);
            setPasswordError(error.response?.data?.error || 'Failed to change password');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteAccount = async () => {
        const password = prompt('Enter your password to confirm account deletion:');
        if (!password) return;

        try {
            setIsSaving(true);
            setDeleteError('');
            await api.delete('/settings/account', { data: { password } });
            localStorage.removeItem('token');
            window.location.href = '/login';
        } catch (error) {
            console.error('Error deleting account:', error);
            setDeleteError(error.response?.data?.error || 'Failed to delete account');
        } finally {
            setIsSaving(false);
            setShowDeleteModal(false);
        }
    };

    const handleExportData = async () => {
        try {
            setExportError('');
            const response = await api.get('/reports/export?format=json');
            const data = JSON.stringify(response.data.data, null, 2);
            const blob = new Blob([data], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `expense-data-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            setExportError('Data exported successfully!');
        } catch (error) {
            console.error('Export error:', error);
            setExportError('Failed to export data');
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading settings...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
                <p className="text-gray-500 mt-1">Manage your account and preferences</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Sidebar */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-1">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200 ${activeTab === tab.id
                                    ? 'bg-indigo-50 text-indigo-600 font-medium'
                                    : 'text-gray-600 hover:bg-gray-50'
                                    }`}
                            >
                                <div className="flex items-center gap-3">
                                    <tab.icon size={20} />
                                    <span>{tab.label}</span>
                                </div>
                                <ChevronRight size={16} />
                            </button>
                        ))}
                    </div>

                    {accountStats && (
                        <div className="mt-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-100">
                            <h3 className="font-semibold text-gray-900 mb-4">Account Stats</h3>
                            <div className="space-y-3">
                                <div>
                                    <p className="text-xs text-gray-600">Total Books</p>
                                    <p className="text-2xl font-bold text-indigo-600">{accountStats.totalLedgers}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-600">Total Transactions</p>
                                    <p className="text-2xl font-bold text-purple-600">{accountStats.totalTransactions}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-600">Member Since</p>
                                    <p className="text-sm font-medium text-gray-800">
                                        {new Date(accountStats.accountCreated).toLocaleDateString('en-IN')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Content */}
                <div className="lg:col-span-3">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                        {/* Profile Tab */}
                        {activeTab === 'profile' && (
                            <div className="space-y-6">
                                <h2 className="text-2xl font-bold text-gray-900">Profile Information</h2>

                                <div className="flex items-center gap-6">
                                    <div className="w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white text-3xl font-bold">
                                        {formData.name ? formData.name.charAt(0).toUpperCase() : 'U'}
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Profile Picture</p>
                                        <p className="text-xs text-gray-400 mt-1">Coming soon</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => handleInputChange('name', e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => handleInputChange('email', e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                                        <input
                                            type="tel"
                                            value={formData.phone}
                                            onChange={(e) => handleInputChange('phone', e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Company/Business</label>
                                        <input
                                            type="text"
                                            value={formData.company}
                                            onChange={(e) => handleInputChange('company', e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                                    <textarea
                                        value={formData.address}
                                        onChange={(e) => handleInputChange('address', e.target.value)}
                                        rows={3}
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>

                                {profileError && (
                                    <p className={`text-sm ${profileError.includes('successfully') ? 'text-green-600' : 'text-red-600'} font-medium`}>
                                        {profileError}
                                    </p>
                                )}

                                <div className="pt-4">
                                    <button
                                        onClick={handleSaveProfile}
                                        disabled={isSaving}
                                        className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50"
                                    >
                                        <Save size={20} />
                                        {isSaving ? 'Saving...' : 'Save Profile'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Notifications Tab */}
                        {activeTab === 'notifications' && (
                            <div className="space-y-6">
                                <h2 className="text-2xl font-bold text-gray-900">Notification Preferences</h2>

                                <div className="space-y-4">
                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                                        <div>
                                            <h3 className="font-medium text-gray-900">Email Notifications</h3>
                                            <p className="text-sm text-gray-500">Receive updates via email</p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={preferences.notifications.email}
                                                onChange={(e) => handleNotificationChange('email', e.target.checked)}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                        </label>
                                    </div>

                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                                        <div>
                                            <h3 className="font-medium text-gray-900">Push Notifications</h3>
                                            <p className="text-sm text-gray-500">Receive push notifications on your device</p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={preferences.notifications.push}
                                                onChange={(e) => handleNotificationChange('push', e.target.checked)}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                        </label>
                                    </div>
                                </div>

                                {preferencesError && (
                                    <p className={`text-sm ${preferencesError.includes('successfully') ? 'text-green-600' : 'text-red-600'} font-medium`}>
                                        {preferencesError}
                                    </p>
                                )}

                                <div className="pt-4">
                                    <button
                                        onClick={handleSavePreferences}
                                        disabled={isSaving}
                                        className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50"
                                    >
                                        <Save size={20} />
                                        {isSaving ? 'Saving...' : 'Save Preferences'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Appearance Tab */}
                        {activeTab === 'appearance' && (
                            <div className="space-y-6">
                                <h2 className="text-2xl font-bold text-gray-900">Appearance</h2>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-3">Theme</label>
                                    <div className="grid grid-cols-2 gap-4">
                                        <button
                                            onClick={() => handlePreferenceChange('theme', 'light')}
                                            className={`p-6 border-2 rounded-xl transition-all ${preferences.theme === 'light'
                                                ? 'border-indigo-600 bg-indigo-50'
                                                : 'border-gray-200 hover:border-gray-300'
                                                }`}
                                        >
                                            <div className="w-full h-24 bg-white rounded-lg border border-gray-200 mb-3"></div>
                                            <p className="font-medium text-gray-900">Light</p>
                                        </button>

                                        <button
                                            onClick={() => handlePreferenceChange('theme', 'dark')}
                                            className={`p-6 border-2 rounded-xl transition-all ${preferences.theme === 'dark'
                                                ? 'border-indigo-600 bg-indigo-50'
                                                : 'border-gray-200 hover:border-gray-300'
                                                }`}
                                        >
                                            <div className="w-full h-24 bg-gray-900 rounded-lg border border-gray-700 mb-3"></div>
                                            <p className="font-medium text-gray-900">Dark</p>
                                            <p className="text-xs text-gray-500 mt-1">(Coming Soon)</p>
                                        </button>
                                    </div>
                                </div>

                                {preferencesError && (
                                    <p className={`text-sm ${preferencesError.includes('successfully') ? 'text-green-600' : 'text-red-600'} font-medium`}>
                                        {preferencesError}
                                    </p>
                                )}

                                <div className="pt-4">
                                    <button
                                        onClick={handleSavePreferences}
                                        disabled={isSaving}
                                        className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50"
                                    >
                                        <Save size={20} />
                                        {isSaving ? 'Saving...' : 'Save Preferences'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Preferences Tab */}
                        {activeTab === 'preferences' && (
                            <div className="space-y-6">
                                <h2 className="text-2xl font-bold text-gray-900">Preferences</h2>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                                        <select
                                            value={preferences.currency}
                                            onChange={(e) => handlePreferenceChange('currency', e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        >
                                            <option value="INR">Indian Rupee (INR)</option>
                                            <option value="USD">$ US Dollar (USD)</option>
                                            <option value="EUR">Euro (EUR)</option>
                                            <option value="GBP">British Pound (GBP)</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Date Format</label>
                                        <select
                                            value={preferences.dateFormat}
                                            onChange={(e) => handlePreferenceChange('dateFormat', e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        >
                                            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                                            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                                            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                                        </select>
                                    </div>
                                </div>

                                {preferencesError && (
                                    <p className={`text-sm ${preferencesError.includes('successfully') ? 'text-green-600' : 'text-red-600'} font-medium`}>
                                        {preferencesError}
                                    </p>
                                )}

                                <div className="pt-4">
                                    <button
                                        onClick={handleSavePreferences}
                                        disabled={isSaving}
                                        className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50"
                                    >
                                        <Save size={20} />
                                        {isSaving ? 'Saving...' : 'Save Preferences'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Security Tab */}
                        {activeTab === 'security' && (
                            <div className="space-y-6">
                                <h2 className="text-2xl font-bold text-gray-900">Security Settings</h2>

                                <div className="space-y-4">
                                    <button
                                        onClick={() => setShowPasswordModal(true)}
                                        className="w-full p-4 bg-gray-50 rounded-xl text-left hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="flex items-center gap-3">
                                            <Key className="text-indigo-600" size={24} />
                                            <div>
                                                <h3 className="font-medium text-gray-900">Change Password</h3>
                                                <p className="text-sm text-gray-500 mt-1">Update your password regularly for security</p>
                                            </div>
                                        </div>
                                    </button>

                                    <div className="p-4 bg-gray-50 rounded-xl">
                                        <div className="flex items-center gap-3">
                                            <Lock className="text-gray-400" size={24} />
                                            <div>
                                                <h3 className="font-medium text-gray-900">Two-Factor Authentication</h3>
                                                <p className="text-sm text-gray-500 mt-1">Coming soon - Add an extra layer of security</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Password Change Modal */}
{showPasswordModal && (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Change Password</h3>

            {passwordError && (
                <p className={`text-sm ${passwordError.includes('successfully') ? 'text-green-600' : 'text-red-600'} font-medium mb-4`}>
                    {passwordError}
                </p>
            )}

            <div className="space-y-4">
                {/* Current Password */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                    <div className="relative">
                        <input
                            type={showCurrent ? "text" : "password"}
                            value={passwordData.currentPassword}
                            onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                            className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <button
                            type="button"
                            onClick={() => setShowCurrent(!showCurrent)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
                        >
                            {showCurrent ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                    </div>
                </div>

                {/* New Password */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                    <div className="relative">
                        <input
                            type={showNew ? "text" : "password"}
                            value={passwordData.newPassword}
                            onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                            className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <button
                            type="button"
                            onClick={() => setShowNew(!showNew)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
                        >
                            {showNew ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                    </div>
                </div>

                {/* Confirm Password */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                    <div className="relative">
                        <input
                            type={showConfirm ? "text" : "password"}
                            value={passwordData.confirmPassword}
                            onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                            className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <button
                            type="button"
                            onClick={() => setShowConfirm(!showConfirm)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
                        >
                            {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex gap-3 mt-6">
                <button
                    onClick={() => {
                        setShowPasswordModal(false);
                        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                        setPasswordError('');
                    }}
                    className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50"
                >
                    Cancel
                </button>

                <button
                    onClick={handleChangePassword}
                    disabled={isSaving}
                    className="flex-1 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium disabled:opacity-50"
                >
                    {isSaving ? 'Changing...' : 'Change Password'}
                </button>
            </div>
        </div>
    </div>
)}

                            </div>
                        )}

                        {/* Data Management Tab */}
                        {activeTab === 'data' && (
                            <div className="space-y-6">
                                <h2 className="text-2xl font-bold text-gray-900">Data Management</h2>

                                <div className="space-y-4">
                                    <button
                                        onClick={handleExportData}
                                        className="w-full p-4 bg-blue-50 border border-blue-200 rounded-xl text-left hover:bg-blue-100 transition-colors"
                                    >
                                        <h3 className="font-medium text-blue-900">Export All Data</h3>
                                        <p className="text-sm text-blue-700 mt-1">Download all your data in JSON format</p>
                                    </button>

                                    {exportError && (
                                        <p className={`text-sm ${exportError.includes('successfully') ? 'text-green-600' : 'text-red-600'} font-medium mt-2`}>
                                            {exportError}
                                        </p>
                                    )}

                                    <button
                                        onClick={() => setShowDeleteModal(true)}
                                        className="w-full p-4 bg-red-50 border border-red-200 rounded-xl text-left hover:bg-red-100 transition-colors"
                                    >
                                        <div className="flex items-center gap-3">
                                            <AlertTriangle className="text-red-600" size={24} />
                                            <div>
                                                <h3 className="font-medium text-red-900">Delete Account</h3>
                                                <p className="text-sm text-red-700 mt-1">Permanently delete your account and all data</p>
                                            </div>
                                        </div>
                                    </button>

                                    {deleteError && (
                                        <p className="text-sm text-red-600 font-medium mt-2">
                                            {deleteError}
                                        </p>
                                    )}
                                </div>

                                {/* Delete Confirmation Modal */}
                                {showDeleteModal && (
                                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                                        <div className="bg-white rounded-2xl p-8 max-w-md w-full">
                                            <div className="flex items-center gap-3 mb-4">
                                                <AlertTriangle className="text-red-600" size={32} />
                                                <h3 className="text-xl font-bold text-gray-900">Delete Account?</h3>
                                            </div>

                                            <p className="text-gray-600 mb-6">
                                                This action cannot be undone. All your data including books, transactions, and settings will be permanently deleted.
                                            </p>

                                            <div className="flex gap-3">
                                                <button
                                                    onClick={() => setShowDeleteModal(false)}
                                                    className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={handleDeleteAccount}
                                                    disabled={isSaving}
                                                    className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium disabled:opacity-50"
                                                >
                                                    {isSaving ? 'Deleting...' : 'Delete Account'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Settings;